import React, { useEffect } from 'react';

/**
 * VideoEmbed Component
 * Handles embedding videos from YouTube and Vimeo
 * 
 * @param {string} url - Full YouTube/Vimeo URL (e.g., "https://youtube.com/watch?v=...")
 * @param {string} type - Video type: "youtube" or "vimeo"
 * @param {string} title - Optional title for the video
 */
const VideoEmbed = ({ url, type, title = null }) => {
  // Extract video ID from YouTube URL (handles multiple formats)
  const getYouTubeVideoId = (youtubeUrl) => {
    if (!youtubeUrl) return null;
    
    // Handle various YouTube URL formats:
    // https://youtube.com/watch?v=VIDEO_ID
    // https://www.youtube.com/watch?v=VIDEO_ID
    // https://youtu.be/VIDEO_ID
    // https://www.youtube.com/embed/VIDEO_ID
    // https://youtu.be/VIDEO_ID?feature=share
    
    // First, try to match youtu.be format (short URL)
    const youtuBeMatch = youtubeUrl.match(/youtu\.be\/([a-zA-Z0-9_-]+)/);
    if (youtuBeMatch && youtuBeMatch[1]) {
      // Extract video ID (remove any query parameters)
      const videoId = youtuBeMatch[1].split('?')[0].split('&')[0];
      if (videoId.length >= 11) {
        return videoId.substring(0, 11); // YouTube IDs are 11 characters
      }
      return videoId;
    }
    
    // Try watch?v= format
    const watchMatch = youtubeUrl.match(/(?:watch\?v=|embed\/|v\/)([a-zA-Z0-9_-]{11})/);
    if (watchMatch && watchMatch[1]) {
      return watchMatch[1];
    }
    
    // Try any v= parameter
    const vParamMatch = youtubeUrl.match(/[?&]v=([a-zA-Z0-9_-]{11})/);
    if (vParamMatch && vParamMatch[1]) {
      return vParamMatch[1];
    }
    
    return null;
  };

  // Extract video ID from Vimeo URL
  const getVimeoVideoId = (vimeoUrl) => {
    if (!vimeoUrl) return null;
    
    // Handle Vimeo URL formats:
    // https://vimeo.com/VIDEO_ID
    // https://player.vimeo.com/video/VIDEO_ID
    
    const patterns = [
      /(?:vimeo\.com\/|player\.vimeo\.com\/video\/)(\d+)/,
      /vimeo\.com\/(\d+)/
    ];
    
    for (const pattern of patterns) {
      const match = vimeoUrl.match(pattern);
      if (match && match[1]) {
        return match[1];
      }
    }
    
    return null;
  };

  // Generate embed URL based on video type
  const getEmbedUrl = () => {
    if (type === 'youtube') {
      const videoId = getYouTubeVideoId(url);
      if (!videoId) {
        console.error(`Failed to extract YouTube video ID from: ${url}`);
        return null;
      }
      // Use nocookie domain and add necessary parameters for better compatibility
      return `https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1`;
    } else if (type === 'vimeo') {
      const videoId = getVimeoVideoId(url);
      if (!videoId) {
        console.error(`Failed to extract Vimeo video ID from: ${url}`);
        return null;
      }
      return `https://player.vimeo.com/video/${videoId}`;
    }
    return null;
  };

  const embedUrl = getEmbedUrl();
  
  // Debug: log the extracted video ID (hooks must be called unconditionally)
  useEffect(() => {
    if (type === 'youtube' && embedUrl && url) {
      const videoId = getYouTubeVideoId(url);
      console.log(`YouTube video URL: ${url}`);
      console.log(`Extracted video ID: ${videoId}`);
      console.log(`Embed URL: ${embedUrl}`);
    }
  }, [url, type, embedUrl]);
  
  // Early returns after hooks
  if (!url || !type) return null;
  
  if (!embedUrl) {
    console.warn(`Invalid ${type} URL: ${url}`);
    return (
      <div className="mb-3" style={{ 
        position: 'relative', 
        paddingBottom: '56.25%', 
        height: 0, 
        overflow: 'hidden', 
        borderRadius: '8px', 
        backgroundColor: '#000',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#fff'
      }}>
        <div>Unable to load video. Invalid URL: {url}</div>
      </div>
    );
  }

  // Log embed details for debugging
  const videoId = type === 'youtube' ? getYouTubeVideoId(url) : null;
  console.log('VideoEmbed render:', { url, type, videoId, embedUrl });

  return (
    <div 
      className="mb-3" 
      style={{ 
        position: 'relative', 
        width: '100%',
        paddingBottom: '56.25%', 
        height: 0,
        borderRadius: '8px', 
        backgroundColor: '#000'
      }}
    >
      <iframe
        src={embedUrl}
        title={title || `Video embed - ${type}`}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          border: 'none',
          display: 'block',
          minHeight: '315px'
        }}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
        frameBorder="0"
      />
      {/* Fallback message if iframe doesn't load */}
      <div 
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          color: '#fff',
          textAlign: 'center',
          display: 'none', // Hidden by default, can be shown if needed
          zIndex: 1
        }}
        id={`video-fallback-${videoId}`}
      >
        <p>Video loading...</p>
        <a 
          href={url} 
          target="_blank" 
          rel="noopener noreferrer"
          style={{ color: '#fff', textDecoration: 'underline' }}
        >
          Open video in new tab
        </a>
      </div>
    </div>
  );
};

export default VideoEmbed;

